import { memo, useCallback, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { useAppList } from "../../hooks/useAppList";
import { CreateAppDialog } from "../../components/CreateAppDialog";
import { CreateButton } from "../../../ui/components/CreateButton";
import { DataGrid } from "../../../ui/components/DataGrid";
import { FunctionViewField } from "../../../ui/components/viewField/FunctionViewField";
import { TextViewField } from "../../../ui/components/viewField/TextViewField";
import { ListContainer } from "../../../ui/containers/ListContainer";
import { TId } from "../../../ui/models";
import { TApp } from "../../models";
import { useNavigate } from "react-router-dom";
import { AUTH_COMPANY_ID } from "../../../company/models";
import { CompanyRole } from "../../../../auth/AuthProvider";

export const AppListScreen = memo(
  (props: { companyRole: string; isSuperAdmin: boolean }) => {
    const { companyRole, isSuperAdmin } = props;
    const companyId = localStorage.getItem(AUTH_COMPANY_ID) as string;
    const [data, loading, error, reload] = useAppList(companyId);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const navigate = useNavigate();

    const handleOpenCreate = useCallback(() => {
      setIsCreateOpen(true);
    }, [setIsCreateOpen]);

    const handleCloseCreate = useCallback(
      (success: boolean) => {
        setIsCreateOpen(false);
        if (success) {
          reload();
        }
      },
      [setIsCreateOpen, reload]
    );

    const onRowClick = useCallback(
      (id: TId) => {
        navigate(`/companies/app/${id}`);
      },
      [navigate]
    );

    return (
      <>
        {error !== null && <Alert severity='error'>{error.toString()}</Alert>}
        <Stack direction='row' spacing={2} justifyContent='end'>
          <CreateButton
            disabled={!(companyRole === CompanyRole.ADMIN || isSuperAdmin)}
            onClick={handleOpenCreate}
          >
            New app
          </CreateButton>
        </Stack>
        <ListContainer data={data?.items} isLoading={loading}>
          <DataGrid onRowClick={onRowClick}>
            <FunctionViewField
              label='Name'
              source='name'
              render={(record: TApp) => (
                <Box>
                  <Typography variant='h6'>{record?.name}</Typography>
                  <Typography variant='body2'>ID: {record?.id}</Typography>
                </Box>
              )}
            />
            <TextViewField
              label='Public Key'
              source='publicKey'
              emptyText='—'
            />
          </DataGrid>
        </ListContainer>
        <CreateAppDialog open={isCreateOpen} onClose={handleCloseCreate} />
      </>
    );
  }
);
